import React, { useCallback, useMemo, useState } from 'react';

import { getLunaSampleUrl } from '@vlabs/api/axios/lp5-client';
import type { Event } from '@vlabs/api/luna-api/model';
import { checkCard, usePutCard } from '@vlabs/api/point-api/client';
import { apiContainer } from '@vlabs/api-bindings';
import {
  ZoomInIcon, DownloadIcon, PlusIcon, SearchIcon,
} from '@vlabs/icons';
import { Sample } from '@vlabs/shared/components/sample/Sample';
import { EventStreamLink } from '@vlabs/shared/components/stream-link/EventStreamLink';
import { useImage } from '@vlabs/shared/hooks';
import { useOutsideClick } from '@vlabs/shared/hooks/useOutsideClick';
import { parseISOToLocale } from '@vlabs/shared/utils/date';
import { Control, useModal, ButtonScrimGroup } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import { routes } from '@vlapps/point/src/app/routes';
import cn from 'classnames';
import qs from 'qs';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import { downloadImage } from '@vlabs/monokit/shared/lib/images';
import { FullFrame } from '@vlabs/monokit/shared/ui/full-frame';
import { FullFrameWithDetections } from '@vlabs/monokit/shared/ui/full-frame-with-detections';

import st from './EventFrame.module.sass';
import { CreateCardForm } from './legacy/CreateCardForm';
import { FullFrameWithInteractiveBBox } from './legacy/FullFrameWithInteractiveBBox';

const initialSampleState = { type: null, state: false };

const getImageUrl = (imageOrigin?: string | null): string | undefined => (
  imageOrigin?.startsWith('/6/images') ? `/api/lp5${imageOrigin}` : undefined
);

export function EventFrame({ event }: { event: Event }): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const putCard = usePutCard();
  const [isSampleVisible, setIsSampleVisible] = useState(initialSampleState);

  const createCardModal = useModal({ size: 'small' });
  const fullFrameModal = useModal({ className: [st.FullFrameModal] });

  const detectionWithFullFrame = event?.face_detections?.find(({ image_origin }) => image_origin?.startsWith('/6/images'))
    ?? event?.body_detections?.find(({ image_origin }) => image_origin?.startsWith('/6/images'));
  const fullFrameUrl = getImageUrl(detectionWithFullFrame?.image_origin);
  const fullFrame = useImage(fullFrameUrl);

  const bodyDetection = event?.body_detections?.find(({ sample_id }) => !!sample_id);
  const bodyDetectionSampleUrl = getLunaSampleUrl('bodies', bodyDetection?.sample_id);

  const faceDetection = event?.face_detections?.find(({ sample_id }) => !!sample_id);
  const faceDetectionSampleUrl = getLunaSampleUrl('faces', faceDetection?.sample_id);

  const uiDetections = useMemo(
    () => [
      bodyDetection && { ...bodyDetection.detection?.rect, type: 'body' },
      faceDetection && { ...faceDetection.detection?.rect, type: 'face' },
    ].filter(Boolean),
    [bodyDetection, faceDetection],
  );

  const sampleDetailRef = useOutsideClick((e) => {
    if (e.target.dataset.overlayAction === 'close') setIsSampleVisible(initialSampleState);
  });

  const onSearch = useCallback(() => {
    history.push({
      pathname: generatePath(routes.search),
      search: qs.stringify({
        references: [{
          id: event.event_id,
          type: 'event',
          referenceType: 'event',
          candidateOrigin: 'events',
        }],
      }),
    });
  }, [event?.event_id, history]);

  const onAddToID = useCallback(async (payload) => {
    const { data: face } = await apiContainer.lunaClient.faces.create({
      ...payload,
      sample_id: faceDetection?.sample_id,
    }, { extract_basic_attributes: 1 });

    try {
      await checkCard(payload.external_id);
    } catch (error) {
      if (error.response.status !== 404) throw error;
      putCard.mutate({
        cardId: payload.external_id,
        data: {
          main_face_id: face.face_id,
        },
      }, {
        onSuccess: () => {
          toast.success(t('lunapoint:add to ID.success'));
          createCardModal.close();
        },
      });
    }
  }, [t, createCardModal, putCard, faceDetection?.sample_id]);

  const onDownloadSamples = useCallback((_, type) => {
    if (!type && fullFrame.url) downloadImage(fullFrame.url);
    if (type === 'face' && faceDetectionSampleUrl) downloadImage(faceDetectionSampleUrl);
    if (type === 'body' && bodyDetectionSampleUrl) downloadImage(bodyDetectionSampleUrl);
  }, [faceDetectionSampleUrl, bodyDetectionSampleUrl, fullFrame.url]);

  const onSampleClick = (type) => {
    setIsSampleVisible({ type, state: true });
  };

  const renderActions = (type) => (
    <ButtonScrimGroup
      className={st.Actions}
      size="small"
    >
      <IconButton
        data-testid="openFullScreenModalButton"
        icon={<ZoomInIcon />}
        kind="secondary"
        onClick={() => fullFrameModal.open()}
        size="small"
        variant="flat"
      />
      <IconButton
        data-testid="workspace.eventDetails.downloadButton"
        icon={<DownloadIcon />}
        kind="secondary"
        onClick={(e) => onDownloadSamples(e, type)}
        size="small"
        variant="flat"
      />
    </ButtonScrimGroup>
  );

  if (!event) return null;

  if (fullFrame.error && !faceDetectionSampleUrl) {
    return (
      <div
        className={st.NoData}
        data-testid="workspace.eventDetails.emptyContent"
      >
        {t('lunapoint:панель оператора.no data')}
      </div>
    );
  }

  return (
    <div className={st.Root}>
      {createCardModal.wrap(<CreateCardForm
        eventSampleUrl={faceDetectionSampleUrl}
        onSubmit={onAddToID}
      />)}
      {fullFrameModal.wrap(
        <FullFrameWithDetections
          data-testid="workspace.eventDetails.fullScreenModal.image"
          detections={{
            type: 'bbox',
            coordinates: uiDetections,
            color: 'positive',
          }}
          src={fullFrame.url}
        />,
      )}
      <div className={st.Header}>
        <div className={st.InfoBlock}>
          <div
            className={st.TimeText}
            data-testid="workspace.eventDetails.createTime"
          >
            {event.create_time ? parseISOToLocale(event.create_time) : '-'}
          </div>
          <div className={st.DotDivider} />
          <div
            className={st.StreamText}
            data-testid="workspace.eventDetails.source"
          >
            <EventStreamLink
              className={st.StreamLink}
              hasTruncatedLink
              hideId
              kind="primary"
              placeholders={{
                removed: t('lunapoint:карточка события.videostream.removed'),
                unavailable: t('lunapoint:карточка события.videostream.unavailable'),
              }}
              size="medium"
              streamId={event.stream_id}
            />
          </div>
        </div>

        <div className={st.EventActions}>
          {!!faceDetectionSampleUrl && (
            <Control.Button
              data-testid="workspace.eventDetails.addToCard"
              kind="secondary"
              onClick={createCardModal.open}
              size="small"
              variant="filled"
            >
              <PlusIcon />
              <div className={st.ButtonContent}>
                {t('lunapoint:кнопки.добавить в ID')}
              </div>
            </Control.Button>
          )}

          <Control.Button
            data-testid="workspace.eventDetails.search"
            kind="secondary"
            onClick={onSearch}
            size="small"
            variant="filled"
          >
            <SearchIcon />
            <div className={st.ButtonContent}>
              {t('lunapoint:кнопки.искать')}
            </div>
          </Control.Button>
        </div>
      </div>

      {!!fullFrame.url && (
        <div className={st.FullFrame}>
          <div className={st.ImageContainer}>
            <div
              className={st.BackgroundImage}
              style={{ backgroundImage: `url(${fullFrame.url})` }}
            />
            <FullFrame
              data-testid="workspace.eventDetails.staticFullFrame"
              src={fullFrame.url}
            />
          </div>

          <div
            className={cn(
              st.InteractiveImageContainer,
              { [st.InteractiveImageContainer_hide]: isSampleVisible.state },
            )}
          >
            <FullFrameWithInteractiveBBox
              detections={uiDetections}
              imageUrl={fullFrame.url}
              onClick={onSampleClick}
              testId="workspace.eventDetails.interactiveFullFrame"
            />

            {renderActions(isSampleVisible.type)}
          </div>

          {!!isSampleVisible.state && (
            <div
              className={st.SampleModal}
              data-overlay-action="close"
            >
              <div
                className={st.SampleWrapper}
                ref={sampleDetailRef}
              >
                <Sample
                  className={cn(st.Sample, st[`Sample_${isSampleVisible.type}`])}
                  controls={renderActions(isSampleVisible.type)}
                  image={isSampleVisible.type === 'face' ? faceDetectionSampleUrl : bodyDetectionSampleUrl}
                  testId={`workspace.eventDetails.modalSampleImage.${isSampleVisible.type}`}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!!fullFrame.error && (
        <div className={st.FullFrameEmpty}>
          {!!faceDetectionSampleUrl && (
            <Sample
              className={cn(st.Sample, st.Sample_face)}
              controls={renderActions('face')}
              image={faceDetectionSampleUrl}
              testId="workspace.eventDetails.sampleImage.face"
            />
          )}

          {!!bodyDetectionSampleUrl && (
            <Sample
              className={cn(st.Sample, st.Sample_body)}
              controls={renderActions('body')}
              image={bodyDetectionSampleUrl}
              testId="workspace.eventDetails.sampleImage.body"
            />
          )}
        </div>
      )}
    </div>
  );
}
